import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  MenuItem,
  TextField,
} from "@mui/material";
import { Edit, Delete } from "@mui/icons-material";
import useLayoutStore from "../../store/useLayoutStore";
import axios from "axios";
import config from "../../config";
import CustomTable from "../../components/common/CustomTable";
import CustomSearch from "../../components/common/CustomSearch";
import CustomPaging from "../../components/common/CustomPaging";
import CustomButton from "../../components/common/CustomButton";
import AddPromotionModal from "./AddPromotionModal";
import UpdatePromotionModal from "./UpdatePromotionModal";

const drawerWidth = 240;
const collapsedWidth = 80;

const PromotionList = () => {
  const { isSidebarOpen } = useLayoutStore();
  const [promotions, setPromotions] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [itemsPerPage, setItemsPerPage] = useState(15);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selectedPromotion, setSelectedPromotion] = useState(null);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openAddModal, setOpenAddModal] = useState(false);

  const fetchPromotions = async () => {
    try {
      const res = await axios.get(`${config.apiBaseUrl}/Discounts`);
      const data = res.data || [];

      setPromotions(
        data.map((item) => ({
          id: item.discountId,
          discountId: item.discountId,
          productId: item.productId,
          categoryId: item.categoryId,
          discountValue: item.discountValue,
          discountType: item.discountType,
          type: item.type ?? "basic",
          conditionJson: item.conditionJson ?? null,
          startDate: item.startDate,
          endDate: item.endDate,
          value: item.discountValue,
          start: new Date(item.startDate).toLocaleDateString("vi-VN"),
          end: new Date(item.endDate).toLocaleDateString("vi-VN"),
        }))
      );
    } catch (err) {
      console.error("Lỗi khi tải khuyến mãi:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPromotions();
  }, []);

  const handleEdit = (promo) => {
    setSelectedPromotion(promo);
    setOpenEditModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Xoá khuyến mãi này?")) {
      try {
        await axios.delete(`${config.apiBaseUrl}/Discounts/${id}`);
        setPromotions((prev) => prev.filter((p) => p.id !== id));
      } catch (err) {
        alert("Xoá thất bại!");
        console.error(err);
      }
    }
  };

  const filtered = promotions.filter((promo) => {
    const matchProduct = promo.productId?.toString().includes(searchQuery);
    const matchType = typeFilter === "all" || promo.type === typeFilter;
    return matchProduct && matchType;
  });

  const startIndex = (page - 1) * itemsPerPage;
  const paginated = filtered.slice(startIndex, startIndex + itemsPerPage);

  const columns = [
    { field: "id", label: "ID", minWidth: "80px" },
    { field: "productId", label: "Product ID", minWidth: "100px" },
    {
      field: "value",
      label: "Discount",
      minWidth: "120px",
      customRender: (row) =>
        row.discountValue?.toLocaleString("vi-VN", {
          style: "currency",
          currency: "VND",
          maximumFractionDigits: 0,
        }) || "—",
    },
    { field: "discountType", label: "Discount Type", minWidth: "120px" },
    { field: "type", label: "Promotion Type", minWidth: "140px" },
    { field: "start", label: "Start Date", minWidth: "120px" },
    { field: "end", label: "End Date", minWidth: "120px" },
    {
      field: "actions",
      label: "Actions",
      minWidth: "150px",
      customRender: (row) => (
        <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
          <IconButton color="secondary" onClick={() => handleEdit(row)}>
            <Edit />
          </IconButton>
          <IconButton color="error" onClick={() => handleDelete(row.id)}>
            <Delete />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box
      sx={{
        flexGrow: 1,
        p: 2,
        width: `calc(100vw - ${
          isSidebarOpen ? drawerWidth : collapsedWidth
        }px)`,
        height: "calc(100vh - 64px)",
        display: "flex",
        flexDirection: "column",
        transition: "width 0.3s",
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h5" fontWeight="bold">
          Promotions
        </Typography>
        <CustomButton
          text="+ Add Promotion"
          onClick={() => setOpenAddModal(true)}
          customColor="#1976d2"
        />
        <AddPromotionModal
          open={openAddModal}
          onClose={() => {
            setOpenAddModal(false);
            fetchPromotions(); // reload sau khi thêm
          }}
        />
      </Box>

      {/* Filter row */}
      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <CustomSearch
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
        />
        <TextField
          select
          label="Filter by Type"
          size="small"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          sx={{ width: 200 }}
        >
          <MenuItem value="all">Tất cả</MenuItem>
          <MenuItem value="basic">Cơ bản</MenuItem>
          <MenuItem value="combo">Combo</MenuItem>
          <MenuItem value="flash">Flash Sale</MenuItem>
          <MenuItem value="quantity">Theo số lượng</MenuItem>
          <MenuItem value="vip">VIP</MenuItem>
          <MenuItem value="birthday">Sinh nhật</MenuItem>
          <MenuItem value="category">Theo danh mục</MenuItem>
        </TextField>
      </Box>

      {/* Table */}
      {loading ? (
        <CircularProgress sx={{ alignSelf: "center", mt: 5 }} />
      ) : (
        <>
          <CustomTable columns={columns} data={paginated} />
          <CustomPaging
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            page={page}
            setPage={setPage}
            totalItems={filtered.length}
          />
          <UpdatePromotionModal
            open={openEditModal}
            onClose={() => setOpenEditModal(false)}
            promotion={selectedPromotion}
            onSave={(updated) => {
              if (!updated || !updated.discountId) return; // <--- tránh lỗi

              setPromotions((prev) =>
                prev.map((p) =>
                  p.id === updated.discountId
                    ? {
                        ...p,
                        productId: updated.productId,
                        categoryId: updated.categoryId,
                        value: updated.discountValue,
                        discountType: updated.discountType,
                        type: updated.type,
                        conditionJson: updated.conditionJson,
                        start: new Date(updated.startDate).toLocaleDateString(
                          "vi-VN"
                        ),
                        end: new Date(updated.endDate).toLocaleDateString(
                          "vi-VN"
                        ),
                      }
                    : p
                )
              );
              setOpenEditModal(false);
            }}
          />
        </>
      )}
    </Box>
  );
};

export default PromotionList;
